//© all rights reserved to 6over6 Ltd. 2022. This software (the "Software") is
the property of 6over6 Ltd. and its licensors. Any use of the Software is
strictly prohibited unless an explicit license or permission is granted by
6over6 Ltd.
<template>
  <div
    id="glasseson_wc-app"
    class="glasseson-global go_fullpage"
    :style="{
      background:
        store.state.page &&
        store.state.page.data &&
        store.state.page.data.targets
          ? 'white'
          : store.state.Style.backgroundColor,
      fontFamily: store.state.Style.fontFamily,
      cursor: showMouse ? 'default' : 'none',
      ...(store.state.Style.zIndex && { zIndex: store.state.Style.zIndex }),
    }"
    @mousemove="handleMouse"
  >
    <div
      @click="close"
      :class="[
        'go_close',
        store.state.Session.options.hideCloseButton ? 'hidden' : null,
      ]"
    >
      <CloseIcon />
    </div>
    <transition name="go_fade" mode="out-in">
      <component
        v-if="store.state.page && !store.state.showResultsPage"
        @close="close()"
        :is="pageComponent"
        class="go_section"
        :page="store.state.page"
        :startAnimation="pageTranstionDelay"
        :key="store.state.page.id"
      />
      <component v-else-if="store.state.showResultsPage" :is="pageComponent" />
    </transition>
    <UnsupportedWebCompanionPage
      v-if="unsupportedPage && !store.state.showResultsPage"
      :pageInfo="unsupportedPage"
      class="go_section"
    />
  </div>
</template>

<script setup>
import { useStore } from "vuex";
import { ref, inject, computed } from "vue";

import HttpClient from "@/../../../common/services/HttpClient.js";
import ActionsEnums from "@/../../../common/enums/ActionsEnums.js";
import CallbackEnums from "@/../../../common/enums/CallbackEnums.js";
import Action from "@/../../../common/models/Action.js";
import CallCallbackAction from "@/../../../common/models/CallCallbackAction.js";
import SocketService from "@/../../../common/services/SocketService";
import ResolutionService from "@/services/ResolutionService.js";

import CloseIcon from "@/components/icons/CloseIcon.vue";

import UnsupportedWebCompanionPage from "./components/sections/UnsupportedWebCompanionPage";
import QRScanPage from "@/components/sections/QrScanPage.vue";
import QRScanHubPage from "@/components/sections/QrScanHubPage.vue";
import GoEyesLandingPageHub from "@/components/sections/GoEyesLandingPageHub.vue";
import TutorialWithLoaderPage from "@/components/sections/TutorialWithLoaderPage.vue";
import MarkersAndTargetsPage from "@/components/sections/MarkersAndTargetsPage.vue";
import DeviceCalibrationV2CheckerboardPage from "@/components/sections/DeviceCalibrationV2CheckerboardPage";
import GoLensesResultPage from "@/components/sections/GoLensesResultPage";
import GoEyesResultPage from "@/components/sections/GoEyesResultPage";

import SetScreenPropertiesAction from "@/../../../common/models/serverActions/SetScreenPropertiesAction.js";
import SetSdkVersionAction from "@/../../../common/models/serverActions/SetSdkVersionAction.js";
import SyncServerAction from "@/../../../common/models/serverActions/SyncServerAction.js";

const store = useStore();
const api = inject("api");
const actionsManager = inject("actionsManager");

const pageTranstionDelay = ref(null);
const showMouse = ref(true);
let mouseTimeout = null;
/** Only schedule once; each reconnect would otherwise restart the 2hr timer */
let qrClientExpiryTimerScheduled = false;

const pageComponent = computed(function () {
  const components = {
    QRScanPage,
    QRScanHubPage,
    GoEyesLandingPageHub,
    TutorialWithLoaderPage,
    MarkersAndTargetsPage,
    DeviceCalibrationV2CheckerboardPage,
    GoLensesResultPage,
    GoEyesResultPage,
  };

  if (store.state.showResultsPage) {
    return components[api.config.appConfig.offline_pages.ResultsPage.type];
  }

  return components[store.state.page.type];
});

const unsupportedPage = computed(function () {
  if (store.state.Resolution.hasZoomed) {
    return api.config.appConfig.offline_pages.UnsupportedZoomPage;
  }

  if (!store.state.Resolution.canMarkersTargetsFit) {
    return api.config.appConfig.offline_pages.UnsupportedWindowSizePage;
  }

  if (store.state.page?.type === "UnsupportedWebCompanionPage") {
    return store.state.page;
  }

  return null;
});

function close() {
  const actions = [new Action(null, ActionsEnums.CloseWebCompanionAction)];
  if (store.state.showResultsPage) {
    actions.push(
      new CallCallbackAction(
        CallbackEnums.CLOSERESULTSPAGE,
        store.state.Results
      )
    );
  } else {
    actions.push(
      new CallCallbackAction(CallbackEnums.CLOSE, store.state.Results)
    );
  }
  actionsManager.addActions(actions);
}

function handleMouse() {
  showMouse.value = true;
  clearTimeout(mouseTimeout);
  mouseTimeout = setTimeout(() => {
    showMouse.value = false;
  }, 2000);
}

function handleByStage(stage, data) {
  const specificActions = {
    onQRScanned: (data) => {
      store.state.Session.userId = data.userId;
      store.state.Session.sessionId = data.sessionId;
      store.state.Results.id = `${data.userId}/${data.sessionId}`;
      HttpClient.defaults.headers.common["GlassesOn-Session-Id"] =
        data.sessionId;

      store.state.customerParameters = data.customerParameters;
      pageTranstionDelay.value = 200;

      return [
        new SetScreenPropertiesAction(store.state.Session.sessionId),
        new SetSdkVersionAction(),
      ];
    },
    onScreenCalibrated: () => {
      store.dispatch("Resolution/setHasScreenCalibrated", true);
    },
    onResultsReady: (data) => {
      store.dispatch("Results/setPrescription", data.result);
    },
    onPdReady: (data) => {
      store.dispatch("Results/setPrescription", data.result);
    },
  };

  if (stage in specificActions) {
    let serverActions = specificActions[stage](data);
    return serverActions ? serverActions : [];
  }

  return [];
}

function handleConnectedSocket() {
  showMouse.value = false;
  store.state.Session.socketId = SocketService.socket.socket_id;
  actionsManager.addActions(api.config.appConfig.first_flow_actions);
  SocketService.setHandler("onActions", (event) => {
    let data = JSON.parse(event.data);
    let serverActionsForStage = handleByStage(data.stage, data);

    let serverActions = [
      new SyncServerAction(data.stage, store.state.Session.sessionId),
      ...serverActionsForStage,
    ];

    if (pageTranstionDelay.value) {
      setTimeout(() => {
        actionsManager.addActions(data.actions);
        pageTranstionDelay.value = null;
      }, pageTranstionDelay.value);
    } else {
      actionsManager.addActions(data.actions);
    }
    let outData = {
      action: "sendServerActions",
      data: serverActions,
    };
    SocketService.socket.send(JSON.stringify(outData));
  });
}

function scheduleQrExpiry() {
  if (qrClientExpiryTimerScheduled) {
    return;
  }
  qrClientExpiryTimerScheduled = true;
  // 1 hr 55 min = (1 * 60 + 55) * 60 = 6900 seconds
  const QR_VALIDITY_SECONDS = (1 * 60 + 55) * 60; // 1 hr 55 min
  if (store.state.Session._qrExpiryTimeoutId) {
    clearTimeout(store.state.Session._qrExpiryTimeoutId);
  }
  // Record the expiry time in epoch milliseconds
  store.state.Session._qrExpiryTimeoutId = setTimeout(() => {
    store.state.Session.qrRefreshNeeded = true;
    store.state.Session._qrExpiryTimeoutId = null;
  }, QR_VALIDITY_SECONDS * 1000);
}

if (!SocketService.socket.connected) {
  SocketService.setHandler("getConnectionId", (data) => {
    SocketService.socket.socket_id = data.socket_id;
    handleConnectedSocket();
    scheduleQrExpiry();
  });
  SocketService.socket.addEventListener("open", () => {
    let outData = {
      action: "getConnectionId",
      data: "",
    };
    SocketService.socket.send(JSON.stringify(outData));
  });
} else {
  handleConnectedSocket();
  scheduleQrExpiry();
}

store.state.Session.useIpadBrowserTargets =
  api.config.appConfig.useIpadBrowserTargets;
ResolutionService.set(store);
</script>

<style lang="scss">
@font-face {
  font-family: "Lexend";
  src: local("Lexend"),
    url(./fonts/hub/Lexend-ExtraBold.woff2) format("truetype");
  font-weight: bolder;
}

@font-face {
  font-family: "Lexend";
  src: local("Lexend"), url(./fonts/hub/Lexend-Bold.woff2) format("truetype");
  font-weight: bold;
}

@font-face {
  font-family: "Lexend";
  src: local("Lexend"), url(./fonts/hub/Lexend-Regular.woff2) format("truetype");
  font-weight: normal;
}

.go_fade-enter-active,
.go_fade-leave-active {
  transition: opacity 0.5s;
}
.go_fade-enter,
.go_fade-leave-to {
  opacity: 0;
}
#glasseson_wc-app {
  font-family: Lexend, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #15192e;
  display: none;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  &.go_fullpage {
    height: 100vh;
    width: 100vw;
    display: flex;
    position: fixed;
    top: 0;
    left: 0;
  }

  .go_section {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 50px;
    touch-action: none;
  }

  .go_close {
    position: fixed;
    top: 0;
    right: 0;
    cursor: pointer;

    &.hidden {
      display: none;
    }
  }
}
</style>
